import {
  BillingCycle,
  SubscriptionPackage,
  SubscriptionPackageDetails,
} from '@/renderer/types/billing';

const DEFAULT_ANNUAL_DISCOUNTS: Record<SubscriptionPackage, number> = {
  starter: 16,
  professional: 16,
  enterprise: 17,
  pro_semi_annual: 16,
};

export const getAnnualDiscountPercentage = (
  packageInfo: Pick<
    SubscriptionPackageDetails,
    'id' | 'annual_discount_percentage'
  >,
) =>
  packageInfo.annual_discount_percentage ||
  DEFAULT_ANNUAL_DISCOUNTS[packageInfo.id] ||
  0;

export const getPackageTotalPriceForCycle = (
  packageInfo: Pick<
    SubscriptionPackageDetails,
    'id' | 'price' | 'annual_discount_percentage' | 'annual_price'
  >,
  billingCycle: BillingCycle,
) => {
  if (billingCycle === 'annual') {
    if (packageInfo.annual_price > 0) {
      return Number(packageInfo.annual_price);
    }

    const annualDiscountPercentage = getAnnualDiscountPercentage(packageInfo);
    if (packageInfo.id === 'pro_semi_annual') {
      return Number(
        (packageInfo.price * 2 * (1 - annualDiscountPercentage / 100)).toFixed(
          2,
        ),
      );
    }
    return Number(
      (packageInfo.price * 12 * (1 - annualDiscountPercentage / 100)).toFixed(
        2,
      ),
    );
  }

  if (billingCycle === 'semi_anually') {
    if (packageInfo.id === 'pro_semi_annual') {
      return Number(packageInfo.price);
    }
    return Number((packageInfo.price * 6).toFixed(2));
  }

  if (packageInfo.id === 'pro_semi_annual') {
    return Number((packageInfo.price / 6).toFixed(2));
  }

  return Number(packageInfo.price);
};

export const getPackageMonthlyEquivalent = (
  packageInfo: Pick<
    SubscriptionPackageDetails,
    'id' | 'price' | 'annual_discount_percentage' | 'annual_price'
  >,
  billingCycle: BillingCycle,
) => {
  const totalPrice = getPackageTotalPriceForCycle(packageInfo, billingCycle);
  if (billingCycle === 'annual') return totalPrice / 12;
  if (billingCycle === 'semi_anually') return totalPrice / 6;
  return totalPrice;
};

export const getPackageAnnualSavingsAmount = (
  packageInfo: Pick<
    SubscriptionPackageDetails,
    'id' | 'price' | 'annual_discount_percentage' | 'annual_price'
  >,
) => {
  const fullMonthlyPrice =
    packageInfo.id === 'pro_semi_annual'
      ? packageInfo.price / 6
      : packageInfo.price;
  const fullPrice = fullMonthlyPrice * 12;
  return Math.max(
    0,
    fullPrice - getPackageTotalPriceForCycle(packageInfo, 'annual'),
  );
};

const billingDescription: Record<BillingCycle, string> = {
  monthly: 'Billed monthly',
  semi_anually: 'Billed every 6 months',
  annual: 'Billed yearly',
};

export const getBillingCycleDescriptor = (billingCycle: BillingCycle) =>
  billingDescription[billingCycle];

export const getBillingCycleActionLabel = (billingCycle: BillingCycle) => {
  if (billingCycle === 'annual') return 'yearly';
  if (billingCycle === 'semi_anually') return 'semi-annually';
  return 'monthly';
};

export const isPackageCycleConfigured = (
  packageInfo: Pick<
    SubscriptionPackageDetails,
    'stripe_price_id' | 'annual_stripe_price_id'
  >,
  billingCycle: BillingCycle,
) =>
  billingCycle === 'annual'
    ? Boolean(packageInfo.annual_stripe_price_id)
    : Boolean(packageInfo.stripe_price_id);
